import { DeliveryPickupStatusType, VisitAuthorizationStatusType } from '@w3lcome/types';
import themes from '_/constants/themes';
import { transparentize } from 'polished';
import React, { useState } from 'react';
import {
  View,
  Image,
  Animated,
  TouchableWithoutFeedback,
  StyleSheet,
  Dimensions,
} from 'react-native';
import {
  HandlerStateChangeEvent,
  PinchGestureHandler,
  PinchGestureHandlerEventPayload,
  State,
} from 'react-native-gesture-handler';
import Modal from 'react-native-modal';

import Spin from '../Spin';
import StatusView from '../StatusView';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import Text from '../Text';

interface PictureContainerProps {
  name?: string;
  status?: VisitAuthorizationStatusType | DeliveryPickupStatusType;
  isVisitCanceled?: boolean;
  pictureUrl?: string | null;
  type?: 'visit' | 'delivery' | 'invitee';
  alreadyCheckout?: boolean;
}

export default function PictureContainer({
  pictureUrl,
  type,
  status,
  name,
  isVisitCanceled,
  alreadyCheckout,
}: PictureContainerProps) {
  const { t: translate } = useTranslation();
  const [visible, setVisible] = useState(false);

  const scale = new Animated.Value(1);

  const onZoomEvent = Animated.event([{ nativeEvent: { scale } }], {
    useNativeDriver: true,
  });

  function onZoomStateChange(event: HandlerStateChangeEvent<PinchGestureHandlerEventPayload>) {
    if (event.nativeEvent.oldState === State.ACTIVE) {
      Animated.spring(scale, {
        toValue: 1,
        useNativeDriver: true,
      }).start();
    }
  }

  const CheckoutWarning = () => (
    <View style={styles.checkoutWarning}>
      <Feather name="alert-triangle" style={{ color: themes.colors.white }} />
      <Text style={styles.statusText}>{translate('visitModal.alreadyCheckout')}</Text>
    </View>
  );

  function renderImage() {
    return (
      <View style={styles.pictureContainer}>
        <View style={styles.avatarBorder}>
          <View style={styles.loadingView}>
            <Spin visible overlayColor="transparent" containerColor="transparent" />
          </View>
          {status === VisitAuthorizationStatusType.AWAITING && alreadyCheckout ? (
            <CheckoutWarning />
          ) : (
            <StatusView
              isVisitCanceled={isVisitCanceled}
              type={type}
              status={status}
              name={name}
              shadow
              containerStyles={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
            />
          )}
          <TouchableWithoutFeedback onPress={() => setVisible(true)}>
            <Image style={styles.avatar} source={{ uri: pictureUrl! }} />
          </TouchableWithoutFeedback>
        </View>
      </View>
    );
  }

  if (!pictureUrl) {
    return status === VisitAuthorizationStatusType.AWAITING && alreadyCheckout ? (
      <CheckoutWarning />
    ) : (
      <StatusView
        isVisitCanceled={isVisitCanceled}
        type={type}
        status={status}
        name={name}
        rounded
        shadow
      />
    );
  }

  return (
    <>
      {renderImage()}
      <Modal
        isVisible={visible}
        style={styles.overlay}
        onBackdropPress={() => setVisible(false)}
        onBackButtonPress={() => setVisible(false)}
      >
        <PinchGestureHandler onGestureEvent={onZoomEvent} onHandlerStateChange={onZoomStateChange}>
          <Animated.Image
            style={[styles.picture, { transform: [{ scale }] }]}
            source={{ uri: pictureUrl }}
          />
        </PinchGestureHandler>
      </Modal>
    </>
  );
}

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  pictureContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatar: {
    zIndex: 1000,
    width: width - 48,
    height: width - 48,
  },
  avatarBorder: {
    overflow: 'hidden',
    shadowColor: themes.colors.black,
    backgroundColor: themes.colors.white,
    marginTop: 8,
    elevation: 5,
    borderRadius: 6,
    shadowRadius: 8,
    width: width - 48,
    height: width - 48,
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 12 },
  },
  overlay: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: transparentize(0.5, themes.colors.black),
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 0,
    zIndex: 1000,
  },
  picture: {
    borderRadius: 8,
    width: width - 24,
    height: width + 24,
    backgroundColor: themes.colors.white,
  },
  loadingView: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  checkoutWarning: {
    alignItems: 'center',
    backgroundColor: themes.colors.warning,
    flexDirection: 'row',
    fontSize: 14,
    height: 32,
    justifyContent: 'center',
  },
  statusText: {
    color: themes.colors.white,
    marginLeft: 8,
  },
});
